/**
 * YoullAlsoLove
 *
 * Home page cross-category recommendation section.
 * Shapermint-style layout: featured main product (left) + 2x2 product grid (right).
 * Lazy-loads via IntersectionObserver to avoid impacting initial page load.
 * Backed by the "youll-also-love" collection in collections-go.
 */

'use client';

import { Link } from 'react-router-dom';
import { ButtonPrimary, Rating, useTheme, ComponentSize } from '@template-project/afrodita-components';
import { ProductCard } from '@/components/molecules/ProductCard';
import { useYoullAlsoLoveProducts } from '@/lib/hooks';
import { LazySection } from '@/components/atoms/LazySection';
import type { YoullAlsoLoveProduct } from '@/lib/hooks';
import { useTranslation } from '@/lib/i18n';
import { useIntl } from '@/lib/intl';
import {
  Section,
  Container,
  SectionTitle,
  ContentRow,
  MainColumn,
  MainImageLink,
  MainImage,
  DescriptionBlock,
  MainTitle,
  MainDescription,
  CTAWrapper,
  GridColumn,
  SkeletonBlock,
  SkeletonText,
} from './YoullAlsoLove.styled';

const GRID_SKELETON_SLOTS = ['a', 'b', 'c', 'd'] as const;

function LoadingSkeleton() {
  return (
    <ContentRow>
      <MainColumn>
        <SkeletonBlock $aspectRatio="3 / 4" />
        <div style={{ padding: '0 8px' }}>
          <SkeletonText $width="70%" />
          <SkeletonText $width="90%" />
          <SkeletonText $width="50%" />
        </div>
      </MainColumn>
      <GridColumn>
        {GRID_SKELETON_SLOTS.map((id) => (
          <div key={id}>
            <SkeletonBlock $aspectRatio="3 / 4" />
            <SkeletonText $width="80%" />
            <SkeletonText $width="40%" />
          </div>
        ))}
      </GridColumn>
    </ContentRow>
  );
}

interface FeaturedProductProps {
  product: YoullAlsoLoveProduct;
  ctaText: string;
}

function FeaturedProduct({ product, ctaText }: Readonly<FeaturedProductProps>) {
  const theme = useTheme();
  const { localizedPath } = useIntl();
  const pdpPath = localizedPath(`/products/${product.slug}`);

  return (
    <>
      <MainImageLink to={pdpPath} aria-label={product.title}>
        <MainImage
          src={product.image}
          alt={product.title}
          loading="lazy"
        />
      </MainImageLink>
      <DescriptionBlock theme={theme}>
        <MainTitle>{product.title}</MainTitle>
        {product.descriptionHtml && (
          <MainDescription dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
        )}
        {product.rating != null && product.reviewCount != null && (
          <Rating
            size={ComponentSize.Small}
            rating={product.rating}
            reviews={product.reviewCount}
          />
        )}
        <CTAWrapper>
          <Link to={pdpPath} style={{ textDecoration: 'none' }}>
            <ButtonPrimary
              text={ctaText}
              size={ComponentSize.Large}
              onClick={() => {}}
            />
          </Link>
        </CTAWrapper>
      </DescriptionBlock>
    </>
  );
}

function YoullAlsoLoveContent() {
  const theme = useTheme();
  const { mainProduct, gridProducts, loading, error } = useYoullAlsoLoveProducts();
  const { t } = useTranslation();

  const hasContent = mainProduct && gridProducts.length > 0;

  if (!loading && (error || !hasContent)) return null;

  return (
    <Section theme={theme}>
      <Container>
        <SectionTitle>{t('home.youllAlsoLove')}</SectionTitle>

        {loading && <LoadingSkeleton />}

        {!loading && !error && hasContent && (
          <ContentRow>
            <MainColumn>
              <FeaturedProduct
                product={mainProduct}
                ctaText={t('home.shopNow')}
              />
            </MainColumn>
            <GridColumn>
              {gridProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  id={product.id}
                  slug={product.slug}
                  name={product.title}
                  image={product.image}
                  price={product.price}
                  compareAtPrice={product.compareAtPrice}
                  rating={product.rating}
                  reviewCount={product.reviewCount}
                />
              ))}
            </GridColumn>
          </ContentRow>
        )}
      </Container>
    </Section>
  );
}

export function YoullAlsoLove() {
  return (
    <LazySection minHeight="520px" ariaLabel="You'll also love section loading">
      <YoullAlsoLoveContent />
    </LazySection>
  );
}
